import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatTabGroup } from '@angular/material';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MaintenancesApi } from '@core/api/maintenances/maintenances.api';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { Maintenance } from '@core/interfaces';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty } from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { sentry } from 'sentry';
import { ConfirmDialogService } from '../../../../../@fuse/components/confirm-dialog/confirm-dialog.service';
import { DialogAlertComponent } from '../dialog-alert/dialog-alert.component';
import { DialogReallocateMaintenanceComponent } from '../dialog-reallocate-maintenance/dialog-reallocate-maintenance.component';
import { DialogReopenMaintenanceComponent } from '../dialog-reopen-maintenance/dialog-reopen-maintenance.component';
import { TabCommentsComponent } from './tabs/comments/tab-comments.component';
import { TabMaintenanceComponent } from './tabs/maintenance/tab-maintenance.component';

export interface MaintenanceDialogData {
  maintenance: Maintenance;
  selectedTabName?: string;
  isViewMode?: boolean;
  selectedTabIndex?: number;
}

@Component({
  selector: 'dialog-edit-maintenances',
  templateUrl: './dialog-maintenances.component.html',
  styleUrls: ['./dialog-maintenances.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class DialogEditMaintenanceComponent implements OnInit, OnDestroy {
  public isViewMode = false;
  public isSavingMaintenance = false;
  public isValidMaintenanceForm = false;

  public refreshDatatable = false;
  public maintenance: Maintenance;
  public selectedTabName: string;
  public pendenciesTotalCount: any;
  public selectedTabIndex: number;
  public hasCreateEditWarrantyPermission;

  public tabIndex = {
    comment: 9
  };

  @ViewChild(MatTabGroup, { static: false })
  public tabGroup: MatTabGroup;

  @ViewChild('tabMaintenances', { static: true })
  private tabMaintenances: TabMaintenanceComponent;

  @ViewChild('tabComments', { static: false })
  private tabComments: TabCommentsComponent;

  @Output()
  isLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

  private destroy$ = new Subject<void>();
  public maintenanceForm: FormGroup;
  public hasLabelViewPermission = this.authenticationService.hasPermission('labels-list');
  public hasPendenciesViewPermission = this.authenticationService.hasPermission('pendencies-list');
  public hasCreateWarrantyPermission = this.authenticationService.hasPermission('warranties-create');
  public hasEditWarrantyPermission = this.authenticationService.hasPermission('warranties-edit');

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MaintenanceDialogData,
    public matDialogRef: MatDialogRef<DialogEditMaintenanceComponent>,
    private fuseProgressBarService: FuseProgressBarService,
    private authenticationService: AuthenticationService,
    private translateService: TranslateService,
    private maintenancesApi: MaintenancesApi,
    private customMessageService: CustomMessagesService,
    private matDialog: MatDialog,
    private confirmDialogService: ConfirmDialogService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.isViewMode = this.data.isViewMode;
    this.maintenance = this.data.maintenance;
    this.selectedTabName = this.data.selectedTabName;
    this.hasCreateEditWarrantyPermission = this.maintenance.status === 'new';
  }

  ngOnInit(): void {
    this.getPendenciesTotalCount();
    const selectedTab = this.route.snapshot.queryParams.selectedTab || this.selectedTabName;
    setTimeout(() => {
      if (selectedTab) {
        this.switchToTab(selectedTab);
      }
    }, 600);
  }

  private switchToTab(tabName: string): void {
    const tabIndex = this.tabGroup._tabs.toArray().findIndex((tab) => tab.textLabel === tabName);
    this.selectedTabIndex = tabIndex;
  }

  ngOnDestroy(): void {
    this.matDialogRef.close(this.refreshDatatable);
    this.destroy$.next();
    this.destroy$.complete();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: null,
      queryParamsHandling: '',
    });
  }

  async getPendenciesTotalCount(): Promise<void> {
    this.pendenciesTotalCount = await this.maintenancesApi.getMaintenancePendenciesTotalCount(this.maintenance.id).toPromise();
  }

  getForm(form: FormGroup): void {
    this.maintenanceForm = form;
    this.isValidMaintenanceForm = form.valid;
  }

  getEquipmentsForm(form: FormGroup): void {
    this.isValidMaintenanceForm = form.valid;
  }

  getDeadlinesForm(form: FormGroup): void {
    if (this.maintenanceForm && this.maintenance.deadlineContract) {
      this.isValidMaintenanceForm = form.valid;
      this.maintenanceForm.get('responseDeadlineViolationReason').setValue(form.get('responseDeadlineViolationReason').value);
      this.maintenanceForm.get('solutionDeadlineViolationReason').setValue(form.get('solutionDeadlineViolationReason').value);
    }
  }

  private toMaintenanceDeadlinesInput(): Record<string, any> {
    const deadlineViolationReasons = {};

    if (!this.maintenance.deadlineContract) {
      return deadlineViolationReasons;
    }

    const isResponseDeadlineLinkedByCompany = this.maintenance.deadlineContract.responseDeadlineViolationReason
      && this.maintenance.deadlineContract.responseDeadlineViolationReason.linkedBy === 'company';

    const isSolutionDeadlineLinkedByCompany = this.maintenance.deadlineContract.solutionDeadlineViolationReason
      && this.maintenance.deadlineContract.solutionDeadlineViolationReason.linkedBy === 'company';

    const isResponseDeadlineViolatedOrCompletedWithViolation = ['violated', 'completedWithViolation'].includes(this.maintenance.responseDeadlineStatus);
    const isSolutionDeadlineViolatedOrCompletedWithViolation = ['violated', 'completedWithViolation'].includes(this.maintenance.solutionDeadlineStatus);

    if (isResponseDeadlineViolatedOrCompletedWithViolation && !isResponseDeadlineLinkedByCompany) {
      const responseDeadlineViolationReason = (this.maintenanceForm.get('responseDeadlineViolationReason').value && this.maintenanceForm.get('responseDeadlineViolationReason').value.id) || null;
      deadlineViolationReasons['responseDeadlineViolationReasonId'] = responseDeadlineViolationReason;
    }

    if (isSolutionDeadlineViolatedOrCompletedWithViolation && !isSolutionDeadlineLinkedByCompany) {
      const solutionDeadlineViolationReason = (this.maintenanceForm.get('solutionDeadlineViolationReason').value && this.maintenanceForm.get('solutionDeadlineViolationReason').value.id) || null;
      deadlineViolationReasons['solutionDeadlineViolationReasonId'] = solutionDeadlineViolationReason;
    }

    return isEmpty(deadlineViolationReasons) ? {} : { deadlineViolationReasons };
  }

  async saveMaintenance(): Promise<void> {
    this.fuseProgressBarService.show();
    this.isSavingMaintenance = true;

    const maintenance = {
      status: this.maintenanceForm.get('status').value,
      typeId: this.maintenanceForm.get('typeId').value,
      message: this.maintenanceForm.get('message').value,
      segmentId: this.maintenanceForm.get('segment').value.id,
      problemId: this.maintenanceForm.get('problemId').value || null,
      locationId: this.maintenanceForm.get('locationId').value || null,
      deadlineContractId: this.maintenanceForm.get('deadlineContract').value ? this.maintenanceForm.get('deadlineContract').value.id : null,
      weight: Number(this.maintenanceForm.get('weight').value) || 1,
      ...this.toMaintenanceDeadlinesInput()
    };

    maintenance['companyId'] = this.maintenanceForm.get('company').value && this.maintenanceForm.get('company').value.id || null;
    maintenance['assigneeId'] = this.maintenanceForm.get('assignee').value ? this.maintenanceForm.get('assignee').value.id : null;
    const maintenanceStatus = this.maintenanceForm.get('status').value;
    if (maintenanceStatus !== 'new' && maintenanceStatus !== 'pending') {
      if (this.maintenanceForm.get('scheduledDate').value) {
        maintenance['scheduledAt'] = this.maintenanceForm.get('scheduledDate').value;
      }
      maintenance['scheduledTime'] = this.maintenanceForm.get('scheduledTime').value || null;
    }
    if (maintenanceStatus === 'done' || maintenanceStatus === 'canceled') {
      if (this.maintenanceForm.get('closedDate').value && this.maintenanceForm.get('closedTime').value) {
        let time = this.maintenanceForm.get('closedTime').value;
        if (time.indexOf(':') === -1) {
          time = time.split('');
          time.splice(time.length - 2, 0, ':');
          time = time.toString().replace(/(,)/g, '');
        }
        const date = moment(this.maintenanceForm.get('closedDate').value).format('YYYY-MM-DD');
        const dateTime = `${date}T${time}`;
        maintenance['closedAt'] = moment(dateTime).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        maintenance['statusDescription'] = this.maintenanceForm.get('statusDescription').value;
      }
    }

    try {
      const hasNewScheduleSuggestionValue = this.maintenanceForm.get('scheduleSuggestionsStatus').value === 'newScheduleSuggestion';
      const isNewOrRequestedMaintenance = this.maintenanceForm.get('status').value === 'new' || this.maintenanceForm.get('status').value === 'requested';
      const isCorporateSuggested = this.maintenanceForm.get('scheduleSuggestionsStatus').value === 'corporateSuggested';
      const scheduleSuggestionsWasChanged = this.maintenanceForm.get('scheduleSuggestionsWasChanged') && this.maintenanceForm.get('scheduleSuggestionsWasChanged').value;

      if (scheduleSuggestionsWasChanged || hasNewScheduleSuggestionValue || (isNewOrRequestedMaintenance && isCorporateSuggested)) {
        this.maintenanceForm.get('scheduleSuggestionsStatus').setValue('corporateSuggested');
        const scheduleSuggestionsMapped = this.maintenanceForm.get('scheduleSuggestions').value.map((scheduleSuggestion) => {
          const date = moment.utc(scheduleSuggestion.date).format('YYYY-MM-DD');
          return { date, time: scheduleSuggestion.time };
        });

        await this.maintenancesApi.updateMaintenanceScheduleSuggestions(this.maintenance.id, scheduleSuggestionsMapped).toPromise();
      }


      await this.maintenancesApi.update(this.maintenance.id, maintenance).toPromise();
      this.refreshDatatable = true;
      this.matDialogRef.close();
      this.customMessageService.success('MAINTENANCES.MAINTENANCE_SUCCESSFULLY_SAVED_MESSAGE');
    } catch (err) {
      this.customMessageService.error('MAINTENANCES.ERRORS.INVALID_MAINTENANCE');
      sentry.captureException(err);
    } finally {
      this.isSavingMaintenance = false;
      this.fuseProgressBarService.hide();
    }
  }

  wasUpdatedLabels(event): void {
    this.refreshDatatable = event;
  }

  openRelocationDialog(): void {
    if (!this.maintenance.company) {
      this.customMessageService.error('MAINTENANCES.MAINTENANCE_REALLOCATE_WITHOUT_COMPANY');
      return;
    }

    const reallocateMaintenanceDialog = this.matDialog.open(DialogReallocateMaintenanceComponent, {
      panelClass: 'dialog-reallocate-maintenance',
      data: this.maintenance,
      hasBackdrop: false
    });

    reallocateMaintenanceDialog.afterClosed().subscribe((reload) => {
      if (reload) {
        this.refreshDatatable = true;
        this.matDialogRef.close(true);
      }
    });
  }

  async archiveMaintenance(): Promise<void> {
    const alertDialog = this.matDialog.open(DialogAlertComponent, {
      panelClass: 'dialog-alert'
    });

    const archived = await alertDialog.afterClosed().toPromise();
    if (!archived) {
      return;
    }

    await this.maintenancesApi.archiveMaintenance(this.maintenance.id);
    this.refreshDatatable = true;
    this.matDialogRef.close(true);
  }

  async unarchiveMaintenance(): Promise<void> {
    await this.maintenancesApi.unarchiveMaintenance(this.maintenance.id);
    this.refreshDatatable = true;
    this.matDialogRef.close(true);
  }

  async openCancelDialog(): Promise<void> {
    const result = await this.confirmDialogService.open({
      title: this.translateService.instant('MAINTENANCES.ALERT.CANCEL.TITLE'),
      message: this.translateService.instant('MAINTENANCES.ALERT.CANCEL.MESSAGE'),
      okText: this.translateService.instant('MAINTENANCES.ALERT.CANCEL.OK_TEXT'),
      cancelText: this.translateService.instant('MAINTENANCES.ALERT.CANCEL.CANCEL_TEXT'),
      inputLabel: this.translateService.instant('MAINTENANCES.ALERT.CANCEL.CANCELATION_REASON_LABEL'),
      inputRequired: true
    });

    if (!result || !result.confirmed) {
      return;
    }

    this.fuseProgressBarService.show();
    this.isSavingMaintenance = true;

    await this.maintenancesApi.cancelMaintenance({
      id: this.maintenance.id,
      cancellationReason: result.message
    });

    this.fuseProgressBarService.hide();
    this.refreshDatatable = true;
    this.isSavingMaintenance = false;
    this.matDialogRef.close(true);
  }

  public openReopenDialog(): void {
    this.matDialog.open(DialogReopenMaintenanceComponent, {
      panelClass: 'field-dialog-860',
      hasBackdrop: false,
      data: {
        isViewMode: this.isViewMode,
        maintenance: this.maintenance
      },
    }).afterClosed().subscribe((reload) => {
      if (reload) {
        this.refreshDatatable = true;
        this.matDialogRef.close(true);
      }
    });
  }

  public onSelectedTabChange(event): void {
    const commentTabIndex = this.tabIndex.comment;
    if (event.index === commentTabIndex) {
      this.tabComments.scrollToBottom();
    }
  }
}
